import { useEffect, useCallback, useRef, useState } from "react";

export function useMusicPreviewPlayer({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playerRef = useRef<any>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!url || typeof window === "undefined") return;

    const initializePlayer = async () => {
      try {
        const shakaModule = await import("shaka-player/dist/shaka-player.ui");
        const shaka = shakaModule.default;

        shaka.polyfill.installAll();

        if (!shaka.Player.isBrowserSupported()) {
          console.error("Browser not supported");
          return;
        }

        const audio = document.createElement("audio");
        const player = new shaka.Player();
        player.attach(audio).catch(console.error);

        const handlePlay = () => setPlaying(true);
        const handlePause = () => setPlaying(false);
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleError = () => setPlaying(false);

        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("error", handleError);
        player.addEventListener("error", handleError);

        audioRef.current = audio;
        playerRef.current = player;

        await player.load(url);

        return () => {
          audio.removeEventListener("play", handlePlay);
          audio.removeEventListener("pause", handlePause);
          audio.removeEventListener("timeupdate", handleTimeUpdate);
          audio.removeEventListener("error", handleError);

          player.destroy().catch(console.error);
          audio.pause();
          audioRef.current = null;
          playerRef.current = null;
        };
      } catch (error) {
        console.error("Failed to initialize player", error);
      }
    };

    let cleanupFn: (() => void) | null = null;

    initializePlayer()
      .then((cleanup) => {
        cleanupFn = cleanup || null;
      })
      .catch((error) => {
        console.error("Failed to initialize player", error);
      });

    return () => cleanupFn?.();
  }, [url]);

  const play = useCallback(async () => {
    try {
      await audioRef.current?.play();
    } catch (error) {
      console.error("Error playing audio", error);
    }
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const seekTo = useCallback(async (position: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = position;
    }
  }, []);

  return {
    playing,
    currentTime,
    play,
    pause,
    seekTo,
  };
}
